import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import React, { useState } from 'react';

import { Input } from '@/components/form/Input';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import { IdentityAccordionHeader } from '@/sections/asset/components/tabs/credentials/credentialForms/IdentityAccordionHeader';
import { IdentityEditor } from '@/sections/asset/components/tabs/credentials/credentialForms/IdentityEditor';
import { IdentityMfaDialog } from '@/sections/asset/components/tabs/credentials/credentialForms/IdentityMfaDialog';
import {
  type ComplexLoginMultiFormData,
  type CredentialIdentity,
  makeEmptyIdentity,
} from '@/sections/asset/components/tabs/credentials/credentialForms/types';
import { type MultiValidationResult } from '@/sections/asset/components/tabs/credentials/credentialForms/validators';

interface IdentitySetManagerProps {
  value: ComplexLoginMultiFormData;
  onChange: (next: ComplexLoginMultiFormData) => void;
  webAppUrl: string;
  validation: MultiValidationResult;
  attempted: boolean;
}

// IdentitySetManager is the single-column accordion body of the credential set
// manager: a shared recorder-hint field, then an Accordion where each identity
// is one collapsible item (header summary + flat editor), and an "Add a login"
// affordance below. Only one identity is expanded at a time. The host owns the
// modal/wizard footer and the "N of M ready" count — this component renders body
// only and fills the height the parent constrains.
export function IdentitySetManager({
  value,
  onChange,
  webAppUrl,
  validation,
  attempted,
}: IdentitySetManagerProps) {
  // openId is the currently-expanded accordion item (non-secret UI state —
  // useState is allowed, R-9). Seeded from the first identity so the first item
  // is open on mount. No useMemo/useCallback: React Compiler handles memoization.
  const [openId, setOpenId] = useState<string>(value.identities[0]?.id ?? '');

  // Per-identity active tab (non-secret UI state, R-9 — mirrors openId). Default
  // 'basics' when an id has no entry, so switching the open identity restores
  // that identity's own last-active tab. No useMemo/useCallback: React Compiler.
  const [activeTabById, setActiveTabById] = useState<
    Record<string, 'basics' | 'credentials' | 'advanced'>
  >({});
  // Which identity's MFA dialog is open (null = closed). No secret enters local
  // state — the dialog reads/writes totpSecret via replaceIdentity below.
  const [mfaTargetId, setMfaTargetId] = useState<string | null>(null);

  // statusOf: 'ready' when the identity is valid; otherwise 'incomplete' (amber)
  // until the user has attempted submit, then 'error' (critical red). This keeps
  // a blank just-added identity from immediately showing the critical dot.
  const errorIds = new Set(validation.identityErrors.map(e => e.id));
  const statusOf = (id: string): 'ready' | 'incomplete' | 'error' => {
    if (!errorIds.has(id)) {
      return 'ready';
    }
    return attempted ? 'error' : 'incomplete';
  };

  const replaceIdentity = (next: CredentialIdentity) => {
    onChange({
      ...value,
      identities: value.identities.map(i => (i.id === next.id ? next : i)),
    });
  };

  const handleAdd = () => {
    const identity = makeEmptyIdentity(webAppUrl);
    onChange({ ...value, identities: [...value.identities, identity] });
    // Auto-open the newly-added identity so the user lands on its form.
    setOpenId(identity.id);
  };

  const handleRemove = (id: string) => {
    const remaining = value.identities.filter(i => i.id !== id);
    onChange({ ...value, identities: remaining });
    if (id === openId) {
      setOpenId(remaining[0]?.id ?? '');
    }
    // Drop the removed identity's UI state so it can't leak onto a reused id.
    setActiveTabById(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
    if (mfaTargetId === id) {
      setMfaTargetId(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Input
        label="Recorder hint (optional)"
        name="recorder-hint"
        value={value.hint}
        onChange={e => onChange({ ...value, hint: e.target.value })}
        placeholder="e.g. Use the Okta SSO button"
        data-testid="recorder-hint-input"
      />

      <Accordion
        type="single"
        collapsible
        value={openId}
        onValueChange={setOpenId}
        className="border-chariot-stroke rounded-md border"
      >
        {value.identities.map(identity => {
          const status = statusOf(identity.id);
          const error =
            validation.identityErrors.find(e => e.id === identity.id) ?? null;
          const label = identity.label.trim() || 'Untitled';
          return (
            <AccordionItem
              key={identity.id}
              value={identity.id}
              className="border-chariot-stroke last:border-b-0"
            >
              {/* The trash control is a SIBLING of the trigger button (never a
                  child) so we don't nest interactive elements. stopPropagation
                  keeps removing from toggling the accordion. */}
              <div className="flex items-center gap-1 pr-3">
                <AccordionTrigger
                  data-testid={`identity-row-${identity.id}`}
                  className="[&>svg]:text-chariot-text-secondary px-3 py-2 hover:no-underline"
                >
                  <IdentityAccordionHeader
                    identity={identity}
                    status={status}
                    error={error}
                  />
                </AccordionTrigger>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={e => {
                    e.stopPropagation();
                    handleRemove(identity.id);
                  }}
                  aria-label={`Remove ${label}`}
                  data-testid={`identity-row-remove-${identity.id}`}
                >
                  <TrashIcon className="size-4" />
                </Button>
              </div>
              <AccordionContent className="px-3">
                <IdentityEditor
                  key={identity.id}
                  identity={identity}
                  onChange={replaceIdentity}
                  attempted={attempted}
                  error={error}
                  activeTab={activeTabById[identity.id] ?? 'basics'}
                  onActiveTabChange={tab =>
                    setActiveTabById(prev => ({ ...prev, [identity.id]: tab }))
                  }
                  onOpenMfa={() => setMfaTargetId(identity.id)}
                />
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      <Button
        variant="ghost"
        size="sm"
        onClick={handleAdd}
        className="w-full justify-start"
        data-testid="identity-add"
      >
        <PlusIcon className="size-4" />
        Add a login
      </Button>

      {/* The MFA dialog is rendered ONCE here (a SIBLING of the Accordion, not
          nested in AccordionContent) so its own z-[150] portal always layers
          above the host Modal's z-[100] overlay regardless of which identity is
          open. It commits totpSecret via replaceIdentity — no new mutation path. */}
      {mfaTargetId !== null &&
        (() => {
          const target = value.identities.find(i => i.id === mfaTargetId);
          if (!target) {
            return null;
          }
          return (
            <IdentityMfaDialog
              open
              identityLabel={target.label}
              value={target.totpSecret}
              onChange={secret =>
                replaceIdentity({ ...target, totpSecret: secret })
              }
              onClose={() => setMfaTargetId(null)}
            />
          );
        })()}
    </div>
  );
}
